import { InventoryItem } from "../components/ui/InventoryItem";
import { inventoryManager } from "../managers/InventoryManager";
import { itemManager } from "../managers/ItemManager";
import { getStatsManager } from "../managers/StatsManager";
import { ContextMenu } from "../components/ui/ContextMenu";

export default class VendorScene extends Phaser.Scene {
    constructor() {
        super({ key: 'VendorScene' });
        this.slots = [];
        this.tooltip = null;
        this.contextMenu = null;
        this.statsManager = null;
        this.vendorData = [];
        this.vendorItems = [];
    }

    init(data) {
        // Get vendor data passed from the calling scene
        this.vendorData = data.vendorData || [];
        
        // Initialize stats manager
        this.statsManager = getStatsManager();
    }

    preload() {
        // Load all item textures and frames
        itemManager.preloadAssets(this);
    }

    create() {
        // Set viewport to constrain UI to 256x192
        this.cameras.main.setViewport(0, 0, 256, 192);
        
        this.input.mouse.disableContextMenu();

        // Create the seed vendor UI background (256x192 image)
        this.background = this.add.image(128, 96, 'seed-vendor-ui');
        this.background.setOrigin(0.5);
        this.background.setScrollFactor(0);
        this.background.setDepth(500);

        // Create hover overlay (initially invisible)
        this.hoverOverlay = this.add.image(0, 0, 'tool-ui-icon-empty-hover');
        this.hoverOverlay.setOrigin(0.5, 0.5); // Explicitly set center origin for pixel-perfect alignment
        this.hoverOverlay.setVisible(false);
        this.hoverOverlay.setScrollFactor(0);
        this.hoverOverlay.setDepth(501); // Between background (500) and seed icons (502)

        // Create seed slots with positions similar to SeedbagUI (4x4 grid)
        this.createSeedSlots();

        this.setupTooltip();
        this.setupContextMenu();
        this.populateVendor();
        this.setupCloseButton();
    }

    createSeedSlots() {
        // Create a 4x4 grid of seed slots matching SeedbagUI exactly
        const positions = [
            // Row 1
            { x: 80, y: 30 }, { x: 113, y: 30 }, { x: 146, y: 30 }, { x: 178, y: 30 },
            // Row 2
            { x: 80, y: 62 }, { x: 113, y: 62 }, { x: 146, y: 62 }, { x: 178, y: 62 },
            // Row 3
            { x: 80, y: 94 }, { x: 113, y: 94 }, { x: 146, y: 94 }, { x: 178, y: 94 },
            // Row 4
            { x: 80, y: 126 }, { x: 113, y: 126 }, { x: 146, y: 126 }, { x: 178, y: 126 }
        ];

        positions.forEach((pos, index) => {
            // Store slot data (no visual slot creation needed since UI image has them)
            this.slots.push({
                x: pos.x-1,
                y: pos.y-1,
                gridX: index % 4,
                gridY: Math.floor(index / 4),
                item: null
            });
        });
    }

    setupTooltip() {
        const rarityColors = {
            'Junk': 0x808080,
            'Normal': 0xFFFFFF,
            'Uncommon': 0x1EFF00,
            'Rare': 0x0070DD,
            'Epic': 0xA335EE,
            'Legendary': 0xFF8000,
            'Quest': 0xFFFF00
        };
        
        this.tooltip = this.add.container(0, 0);
        this.tooltip.setAlpha(0.8);
        this.tooltip.setDepth(1000);
        this.tooltip.setScrollFactor(0);
        
        this.tooltipBg = this.add.rectangle(0, 0, 155, 40, 0x78716C)
            .setOrigin(0)
            .setStrokeStyle(2, 0xffffff);
        this.tooltip.add(this.tooltipBg);
        
        this.titleText = this.add.text(3, 3, '', { 
            fontSize: '12px',
            fontStyle: 'bold',
            fill: '#ffffff',
            wordWrap: { width: 140 }
        });
        
        this.rarityTypeText = this.add.text(3, 20, '', {
            fontSize: '12px',
            fontStyle: 'italic',
        });
        
        this.priceText = this.add.text(3, 35, '', {
            fontSize: '12px',
            fill: '#ffff00' // Yellow for price
        });
        
        this.descriptionText = this.add.text(3, 50, '', {
            fontSize: '12px',
            fill: '#ffffff',
            wordWrap: { width: 140 }
        });
        
        this.tooltip.add([this.titleText, this.rarityTypeText, this.priceText, this.descriptionText]);
        this.tooltip.setVisible(false);

        this.input.on('gameobjectover', (pointer, gameObject) => {
            if (gameObject instanceof InventoryItem && gameObject.vendorData) {
                this.updateTooltip(gameObject, rarityColors);
                this.tooltip.setVisible(true);
                
                // Show hover overlay at original slot position (sprites were moved up 5px)
                // Use Math.round to ensure pixel-perfect positioning
                this.hoverOverlay.setPosition(Math.round(gameObject.x), Math.round(gameObject.y + 5));
                this.hoverOverlay.setVisible(true);
            }
        });

        this.input.on('gameobjectout', (pointer, gameObject) => {
            if (gameObject instanceof InventoryItem) {
                this.tooltip.setVisible(false);
                this.hoverOverlay.setVisible(false);
            }
        });
    }

    updateTooltip(gameObject, rarityColors) {
        const vendorData = gameObject.vendorData;
        this.titleText.setText(gameObject.itemName);
        this.titleText.setColor('#' + rarityColors[gameObject.rarity].toString(16));
        this.rarityTypeText.setText(`${gameObject.rarity} ${gameObject.itemType}`);
        this.rarityTypeText.setColor('#1C1917');
        
        // Show price instead of value
        const priceText = vendorData.quantity === -1 ? 
            `Price: ¥${vendorData.price} (unlimited stock)` : 
            `Price: ¥${vendorData.price} (${vendorData.quantity} in stock)`;
        this.priceText.setText(priceText);
        
        this.descriptionText.setText(gameObject.description);
        this.descriptionText.setFontStyle("italic");

        // Calculate tooltip dimensions and position
        let totalHeight = 3 + 
            this.titleText.height + 
            this.rarityTypeText.height +
            this.priceText.height +
            this.descriptionText.height + 
            12;
        
        const largestWidth = Math.max(
            this.titleText.width, 
            this.rarityTypeText.width, 
            this.priceText.width,
            this.descriptionText.width
        );

        this.tooltipBg.setSize(largestWidth + 6, totalHeight);

        const UI_WIDTH = 256;
        const UI_HEIGHT = 192;
        const TOOLTIP_MARGIN = 5;

        let tooltipX = gameObject.x + 16;
        let tooltipY = gameObject.y + 16;

        // Adjust horizontal position
        if (tooltipX + largestWidth > UI_WIDTH) {
            tooltipX = gameObject.x - largestWidth - TOOLTIP_MARGIN;
        }

        if (tooltipX < 0) {
            tooltipX = TOOLTIP_MARGIN;
        }

        // Adjust vertical position
        if (tooltipY + totalHeight > UI_HEIGHT) {
            tooltipY = gameObject.y - totalHeight - TOOLTIP_MARGIN;
        }

        if (tooltipY < 0) {
            tooltipY = TOOLTIP_MARGIN;
        }

        this.tooltip.setPosition(tooltipX, tooltipY);
    }

    setupContextMenu() {
        this.input.on('pointerdown', (pointer) => {
            if (pointer.rightButtonDown()) {
                const objectsUnderPointer = this.input.hitTestPointer(pointer);
                const vendorItem = objectsUnderPointer.find(obj => obj instanceof InventoryItem && obj.vendorData);
                
                if (vendorItem) {
                    this.showContextMenu(vendorItem, pointer.x, pointer.y);
                }
            } else {
                this.hideContextMenu();
            }
        });
    }

    showContextMenu(item, x, y) {
        this.hideContextMenu();

        const vendorData = item.vendorData;
        const options = [];

        // Check if player can afford and has space
        const playerMoney = this.statsManager.getStats().money || 0;
        //const hasInventorySpace = inventoryManager.hasInventorySpace();

        // Add buy options
        [1, 5, 10].forEach(quantity => {
            const totalCost = vendorData.price * quantity;
            const canAfford = playerMoney >= totalCost;
            const inStock = vendorData.quantity === -1 || vendorData.quantity >= quantity;
            
            let optionText = `Buy x${quantity} - ¥${totalCost}`;
            if (!canAfford) optionText += ' (Not enough ¥)';
            if (!inStock) optionText += ' (Not in stock)';
            //if (!hasInventorySpace) optionText += ' (No space)';

            options.push({
                text: optionText,
                callback: canAfford && inStock ? 
                    () => this.buyItem(item, quantity) : null
            });
        });

        if (options.length > 0) {
            this.contextMenu = new ContextMenu(this, x, y, options);
            this.contextMenu.show(x, y);
        }
    }

    hideContextMenu() {
        if (this.contextMenu) {
            this.contextMenu.destroy();
            this.contextMenu = null;
        }
    }

    async buyItem(item, quantity) {
        this.hideContextMenu();
        
        const vendorData = item.vendorData;
        const totalCost = vendorData.price * quantity;
        
        try {
            // Check if player has enough money
            const currentStats = this.statsManager.getStats();
            if (currentStats.money < totalCost) {
                return;
            }

            // Check inventory space
            // if (!inventoryManager.hasInventorySpace()) {
            //     console.log('Inventory full!');
            //     return;
            // }

            // Deduct money from player
            await this.statsManager.updateMoney(totalCost, 'subtract');
            
            // Add items to inventory as a single bulk operation
            await inventoryManager.addItem({
                itemCode: vendorData.itemCode,
                categoryCode: vendorData.categoryCode,
                quantity: quantity
            });
            
            // Update vendor stock if not unlimited
            if (vendorData.quantity !== -1) {
                vendorData.quantity -= quantity;
                if (vendorData.quantity <= 0) {
                    // Remove item from vendor or mark as out of stock
                    item.setTint(0x666666); // Gray out
                }
            }

            
            // Play purchase sound
            this.sound.play('success', { volume: 0.5 });
            
        } catch (error) {
            console.error('Error purchasing item:', error);
        }
    }

    getSeedIdFromCropId(cropId) {
        // Map crop IDs to seed IDs
        const cropToSeedMap = {
            38: 1, // Onion -> Onion Seeds
            36: 2, // Carrot -> Carrot Seeds  
            39: 3, // Potato -> Potato Seeds
            37: 4  // Corn -> Corn Seeds
        };
        return cropToSeedMap[cropId];
    }

    populateVendor() {
        let slotIndex = 0;
        const maxSlots = 16; // 4x4 grid limit
        
        this.vendorData.forEach(vendorItem => {
            if (slotIndex >= maxSlots || slotIndex >= this.slots.length) return; // No more slots in 4x4 grid
            
            const slot = this.slots[slotIndex];
            
            // Get seed item data directly
            const seedItemCode = `Seeds_${this.getSeedIdFromCropId(vendorItem.cropId)}`;
            const seedItemData = itemManager.getItemData(seedItemCode);
            if (!seedItemData || seedItemData.type !== 'Seeds') {
                console.warn('Seed item not found or invalid:', vendorItem.cropId, seedItemCode);
                return;
            }

            // Create seed item using actual seed data (move sprite up 5 pixels)
            const seedItem = this.createSeedItem(seedItemData, vendorItem, slot.x, slot.y - 5);
            if (seedItem) {
                slot.item = seedItem;
                slotIndex++;
            }
        });
    }

    createSeedItem(seedItemData, vendorData, x, y) {
        try {
            // Use crop spritesheet directly with frame 1 (0-indexed) for seedbag icon
            const cropTextureKey = seedItemData.textureKey; // e.g., "potato"
            
            // Create inventory item for the seed
            const item = new InventoryItem(
                this,
                x,
                y,
                cropTextureKey, // Use crop texture directly
                seedItemData.name,
                seedItemData.rarity,
                seedItemData.type,
                seedItemData.description,
                seedItemData.value
            );
            
            // Set frame 1 (0-indexed) for seedbag icon
            item.setFrame(1);

            // Add vendor-specific data using seed item data
            // Extract numeric ID from "Seeds_X" format for backend compatibility
            const numericId = parseInt(seedItemData.id.split('_')[1]);
            item.vendorData = {
                itemCode: numericId, // Numeric seed item ID (e.g., 3 from "Seeds_3")
                categoryCode: 1, // Seeds category
                price: seedItemData.value, // Use seed price from seed data
                quantity: vendorData.quantity || -1, // -1 means unlimited
                farmingLevel: seedItemData.farmingLevelRequired || 0,
                cropId: seedItemData.cropId,
                displayAsSeedBag: true // Mark this item to display as seed bag in inventory
            };

            // Check if locked based on farming level
            const playerFarmingLevel = 0; // TODO: Get actual farming level
            if (seedItemData.farmingLevelRequired && playerFarmingLevel < seedItemData.farmingLevelRequired) {
                item.setTint(0x666666); // Desaturate locked items
            }

            item.setInteractive();
            item.setDepth(502); // Higher than background (500) to appear on top
            this.add.existing(item);

            return item;
            
        } catch (error) {
            console.error('Error creating seed item:', error);
            return null;
        }
    }

    setupCloseButton() {
        // Add close button (X) positioned for the new UI
        const closeButton = this.add.text(208, 6, 'X', {
            fontSize: '16px',
            fill: '#ffffff',
            fontStyle: 'bold'
        }).setOrigin(0.5)
          .setInteractive()
          .setDepth(1001)
          .setScrollFactor(0);

        closeButton.on('pointerover', () => closeButton.setTint(0xdddddd));
        closeButton.on('pointerout', () => closeButton.clearTint());
        closeButton.on('pointerdown', () => this.closeVendor());
        this.input.keyboard.on('keydown-ESC', async () => {
            this.closeVendor();
        });
    }

    closeVendor() {
        // Reset camera viewport before closing
        this.cameras.main.setViewport(0, 0, 256, 192);
        
        this.scene.stop();
        this.scene.resume('Farm'); // Resume the farm scene
    }
} 